// ============================================================
// PasswordPage — 密碼管理（變更自己密碼 + 主管重設員工）
// ============================================================

import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

interface PasswordStatus {
  mustChange: boolean;
  isDefaultPassword: boolean;
  ageDays: number;
  lastUpdatedAt: string;
}

interface StaffRow {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
}

export function PasswordPage() {
  const { user } = useAuthStore();
  const isManager = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const [status, setStatus] = useState<PasswordStatus | null>(null);
  const [tab, setTab] = useState<'self' | 'pin' | 'reset'>('self');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [pinExists, setPinExists] = useState(false);
  const [pinMsg, setPinMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [staff, setStaff] = useState<StaffRow[]>([]);

  // 自己改密碼
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // 重設員工密碼
  const [resetTarget, setResetTarget] = useState<StaffRow | null>(null);

  useEffect(() => {
    api.get<PasswordStatus>('/password/status').then(setStatus).catch(() => {});
    if (isManager) {
      api.get<{ staff: StaffRow[] }>('/staff').then(r => setStaff(r.staff)).catch(() => {});
    }
    // 檢查自己是否已設 PIN
    if (user) {
      api.get<{ staff: { id: string; hasPin: boolean }[] }>(`/pin-auth/staff?slug=tianshui`)
        .then(r => {
          const me = r.staff.find(s => s.id === user.id);
          setPinExists(!!me?.hasPin);
        })
        .catch(() => {});
    }
  }, [isManager, user]);

  const handleSetPin = async () => {
    setPinMsg(null);
    if (!/^\d{4,6}$/.test(pin)) {
      setPinMsg({ type: 'err', text: 'PIN 必須為 4-6 位數字' });
      return;
    }
    if (pin !== pin2) {
      setPinMsg({ type: 'err', text: '兩次 PIN 不一致' });
      return;
    }
    try {
      await api.post('/pin-auth/set', { pin });
      setPinMsg({ type: 'ok', text: pinExists ? 'PIN 已更新' : 'PIN 已設定，下次登入可用 PIN 快速登入' });
      setPin(''); setPin2('');
      setPinExists(true);
    } catch (err) {
      setPinMsg({ type: 'err', text: (err as Error).message });
    }
  };

  const handleClearPin = async () => {
    if (!confirm('確定要清除 PIN？清除後將無法用 PIN 登入。')) return;
    try {
      await api.delete('/pin-auth');
      setPinMsg({ type: 'ok', text: 'PIN 已清除' });
      setPinExists(false);
    } catch (err) {
      setPinMsg({ type: 'err', text: (err as Error).message });
    }
  };

  const handleChangePassword = async () => {
    setMsg(null);
    if (newPwd !== newPwd2) {
      setMsg({ type: 'err', text: '兩次新密碼不一致' });
      return;
    }
    try {
      await api.post('/password/change', { oldPassword: oldPwd, newPassword: newPwd });
      setMsg({ type: 'ok', text: '密碼已更新！下次登入請使用新密碼' });
      setOldPwd(''); setNewPwd(''); setNewPwd2('');
      const s = await api.get<PasswordStatus>('/password/status');
      setStatus(s);
    } catch (err) {
      setMsg({ type: 'err', text: (err as Error).message });
    }
  };

  const handleResetStaff = async (target: StaffRow) => {
    if (!confirm(`確定重設 ${target.name} 的密碼為預設 password123？員工首次登入時會被要求變更。`)) return;
    try {
      const res = await api.post<{ message: string }>('/password/reset', { userId: target.id });
      alert(res.message);
    } catch (err) {
      alert('重設失敗：' + (err as Error).message);
    }
  };

  const handleResetStaffPin = async (target: StaffRow) => {
    const pin = prompt(`請輸入 ${target.name} 的新 PIN（4-6 位數字）：`, '0000');
    if (!pin) return;
    if (!/^\d{4,6}$/.test(pin)) {
      alert('PIN 必須為 4-6 位數字');
      return;
    }
    try {
      const res = await api.post<{ message: string }>('/pin-auth/reset', { userId: target.id, pin });
      alert(res.message + `\n\n請告知員工新 PIN：${pin}（建議員工首次使用後立即修改）`);
    } catch (err) {
      alert('重設失敗：' + (err as Error).message);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      <h1 className="text-xl font-bold text-morandi-800">密碼與安全</h1>

      {/* 首次登入警告 */}
      {status?.mustChange && (
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
          <span className="text-lg">⚠</span>
          <div>
            <div className="font-medium">你仍在使用預設密碼</div>
            <div className="text-xs">為了安全，請立即變更為自己的密碼。預設密碼任何人都可能猜到。</div>
          </div>
        </div>
      )}

      {/* Tab */}
      <div className="flex gap-2 border-b border-morandi-200">
        <button
          onClick={() => setTab('self')}
          className={`px-4 py-2 text-sm transition-colors ${
            tab === 'self'
              ? 'text-morandi-800 border-b-2 border-earth-400 font-medium'
              : 'text-morandi-400 hover:text-morandi-600'
          }`}
        >密碼</button>
        <button
          onClick={() => setTab('pin')}
          className={`px-4 py-2 text-sm transition-colors ${
            tab === 'pin'
              ? 'text-morandi-800 border-b-2 border-earth-400 font-medium'
              : 'text-morandi-400 hover:text-morandi-600'
          }`}
        >PIN 碼 {pinExists && <span className="text-xs text-sage-500 ml-1">●</span>}</button>
        {isManager && (
          <button
            onClick={() => setTab('reset')}
            className={`px-4 py-2 text-sm transition-colors ${
              tab === 'reset'
                ? 'text-morandi-800 border-b-2 border-earth-400 font-medium'
                : 'text-morandi-400 hover:text-morandi-600'
            }`}
          >重設員工密碼</button>
        )}
      </div>

      {/* PIN 碼設定 */}
      {tab === 'pin' && (
        <div className="card-morandi p-5 space-y-4 max-w-md">
          <div>
            <h3 className="text-base font-semibold text-morandi-800 mb-1">
              {pinExists ? '變更 PIN' : '設定 PIN'}
            </h3>
            <p className="text-xs text-morandi-400">
              PIN 是 4-6 位數字，下次開 App 不用輸入 Email + 密碼，直接點頭像 + PIN 即可登入。
            </p>
          </div>

          <div>
            <label className="text-sm text-morandi-500 block mb-1">新 PIN（4-6 位數字）</label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              className="input-field text-2xl tracking-widest text-center font-mono"
              placeholder="• • • •"
            />
          </div>
          <div>
            <label className="text-sm text-morandi-500 block mb-1">再輸入一次</label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin2}
              onChange={e => setPin2(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              className="input-field text-2xl tracking-widest text-center font-mono"
              placeholder="• • • •"
            />
          </div>

          {pinMsg && (
            <div className={`text-sm ${pinMsg.type === 'ok' ? 'text-sage-600' : 'text-rose-500'}`}>
              {pinMsg.text}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleSetPin}
              disabled={!pin || !pin2}
              className="flex-1 px-4 py-2 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm disabled:opacity-50"
            >{pinExists ? '更新 PIN' : '設定 PIN'}</button>
            {pinExists && (
              <button
                onClick={handleClearPin}
                className="px-4 py-2 bg-white border border-rose-300 text-rose-500 hover:bg-rose-50 rounded-btn text-sm"
              >清除 PIN</button>
            )}
          </div>
        </div>
      )}

      {/* 變更自己密碼 */}
      {tab === 'self' && (
        <div className="card-morandi p-5 space-y-4 max-w-md">
          <div>
            <label className="text-sm text-morandi-500 block mb-1">舊密碼</label>
            <input
              type="password"
              value={oldPwd}
              onChange={e => setOldPwd(e.target.value)}
              className="input-field"
              placeholder="請輸入目前密碼"
            />
          </div>
          <div>
            <label className="text-sm text-morandi-500 block mb-1">新密碼</label>
            <input
              type="password"
              value={newPwd}
              onChange={e => setNewPwd(e.target.value)}
              className="input-field"
              placeholder="至少 8 字元，含英文與數字"
            />
          </div>
          <div>
            <label className="text-sm text-morandi-500 block mb-1">確認新密碼</label>
            <input
              type="password"
              value={newPwd2}
              onChange={e => setNewPwd2(e.target.value)}
              className="input-field"
              placeholder="再輸入一次新密碼"
            />
          </div>
          {msg && (
            <div className={`text-sm ${msg.type === 'ok' ? 'text-sage-600' : 'text-rose-500'}`}>
              {msg.text}
            </div>
          )}
          <button
            onClick={handleChangePassword}
            disabled={!oldPwd || !newPwd || !newPwd2}
            className="px-4 py-2 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm disabled:opacity-50"
          >變更密碼</button>

          {status && !status.mustChange && (
            <div className="text-xs text-morandi-400 pt-2 border-t border-morandi-100">
              上次異動：{new Date(status.lastUpdatedAt).toLocaleDateString('zh-TW')}
              （{status.ageDays} 天前）
            </div>
          )}
        </div>
      )}

      {/* 管理端：重設員工密碼 */}
      {tab === 'reset' && isManager && (
        <div className="card-morandi overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-morandi-50 text-morandi-500 text-xs">
                <th className="text-left px-4 py-3 font-medium">員工</th>
                <th className="text-left px-2 py-3 font-medium">Email</th>
                <th className="text-center px-2 py-3 font-medium">角色</th>
                <th className="text-right px-4 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {staff.filter(s => s.isActive && s.id !== user?.id).map(s => (
                <tr key={s.id} className="border-t border-morandi-100 hover:bg-morandi-50">
                  <td className="px-4 py-3 font-medium text-morandi-700">{s.name}</td>
                  <td className="px-2 py-3 text-morandi-500 text-xs">{s.email}</td>
                  <td className="text-center px-2 py-3">
                    <span className="badge text-xs bg-morandi-100 text-morandi-600">{s.role}</span>
                  </td>
                  <td className="text-right px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleResetStaff(s)}
                        className="text-xs text-rose-500 hover:text-rose-700"
                      >重設密碼</button>
                      <button
                        onClick={() => handleResetStaffPin(s)}
                        className="text-xs text-earth-500 hover:text-earth-700"
                      >重設 PIN</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs text-morandi-400 bg-morandi-50">
            提示：重設後員工的密碼會變為 <code className="px-1 bg-white rounded">password123</code>，首次登入時系統會提示他們變更。
          </div>
        </div>
      )}
    </div>
  );
}
